import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock } from "lucide-react";
import Alert from "../../components/alert";
import "./styles/login.css";
import Footer from "../../components/footer";
import Navbar from "../../components/navbar";
import { ChedaGamingForm } from "../../components/form/cheda-gaming-form";
import ChedaGamingInput from "../../components/form/cheda-gaming-input";
import ChedGamingButton from "../../components/cheda-gaming-button";
import * as Yup from "yup";
import { login } from "../../common/services/auth";
import { useAuth } from "../../common/store/authStore";
import ChedaGamingPasswordInput from "../../components/form/cheda-gaming-password-input";
import { fetchUserCountry } from "../../common/services/geo";

const LoginPage = () => {
  useEffect(() => {
    document.title = "Login - Cheda Gaming";
  }, []);

  const [alert, setAlert] = useState<any | null>(null);
  const navigate = useNavigate();

  const { setAuthenticatedUser, setAccessToken, setRefreshToken } = useAuth();

  const showAlert = (
    type: any,
    message: string,
    title: string | null = null,
  ) => {
    setAlert({ type, message, title });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const handleLogin = async (
    values: { email: string; password: string },
    helpers: any,
  ) => {
    setAlert(null);
    try {
      const country = await fetchUserCountry();
      const payload = { 
        email: values.email, 
        password: values.password,
        country: country || undefined
      };

      const res = await login(payload);
      // expect res to have user and accessToken
      setAuthenticatedUser(res.user || null);
      setAccessToken(res.accessToken || null);
      setRefreshToken(res.refreshToken || null);

      // TEMP: Skip OTP redirect, always log in
      navigate("/tournaments");
    } catch (error: any) {
      showAlert("error", error?.message || "Login failed", "Login Failed");
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <div className="login-page page-with-navbar-footer">
      {/* Navbar */}
      <Navbar />

      {/* Gaming Background */}
      <div className="login-background">
        <div className="background-overlay"></div>
      </div>

      {/* Main Content */}
      <div className="login-container">
        <div className="login-card">
          {/* Logo Section */}
          <div className="login-header">
            <div className="logo-container">
              <img
                src="/images/chedagaming_logo.png"
                alt="Cheda Gaming"
                className="login-logo"
              />
            </div>
            <h1 className="login-title">Welcome Back, Gamer</h1>
            <p className="login-subtitle">
              Ready to dominate? Sign in and join the battle
            </p>
          </div>

          {/* Login Form */}
          <ChedaGamingForm
            initialValues={{ email: "", password: "" }}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            {(formik) => (
              <>
                <ChedaGamingInput
                  name="email"
                  label="Email Address"
                  placeholder="Enter your email"
                  required
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  touched={!!formik.touched.email}
                  error={formik.errors.email as string | undefined}
                  disabled={formik.isSubmitting}
                  icon={<Mail className="input-icon" size={20} />}
                />

                <ChedaGamingPasswordInput
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  required
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  touched={!!formik.touched.password}
                  error={formik.errors.password as string | undefined}
                  disabled={formik.isSubmitting}
                  icon={<Lock className="input-icon" size={20} />}
                />

                <div className="form-options">
                  <Link to="/forget-password" className="forgot-password">
                    Forgot your password?
                  </Link>
                </div>

                <ChedGamingButton
                  type="submit"
                  className="login-button"
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                >
                  Sign In
                </ChedGamingButton>
              </>
            )}
          </ChedaGamingForm>

          {/* Sign Up Link */}
          <div className="signup-section">
            <p className="signup-text">
              Don't have an account?{" "}
              <Link to="/sign-up" className="signup-link">
                Create one now
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={hideAlert}
          duration={5000}
          position="top-right"
        />
      )}
    </div>
  );
};

export default LoginPage;
